import { Profile } from '@creditsights/api-ui-web';
import { Button, ButtonMode, ButtonSize, Dropdown } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import { ReactElement, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useSetRecoilState } from 'recoil';
import { toasterState } from '../../../atoms/toaster.atom';
import { USER_PROFILE_CACHE_KEY } from '../../../Constants';
import useSaveProfile from '../../../hooks/user/useSaveProfile';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
function PersonalInformation({ user, roles }): ReactElement {
  const queryClient = useQueryClient();
  const showToaster = useSetRecoilState(toasterState);
  const {
    register,
    control,
    watch,
    formState: { errors },
    handleSubmit,
  } = useForm<Profile>({ defaultValues: user });
  const role = watch('role');
  const [options, setOptions] = useState<any[]>([]);
  const { mutate: saveProfile, isLoading: isSaveLoading, isSuccess, reset } = useSaveProfile();

  useEffect(() => {
    setOptions(
      roles.map((role) => ({
        key: role.code,
        text: role.label,
        value: role.code,
      }))
    );
  }, [roles]);

  useEffect(() => {
    if (isSuccess) {
      showToaster({
        text: 'Your personal information was saved',
      });
      queryClient.invalidateQueries([USER_PROFILE_CACHE_KEY]);
      reset();
    }
  }, [isSuccess]);

  const selectRole = (v) => {
    const role = roles.find((p) => p.code === v);
    return {
      freeText: null,
      code: role.code,
    };
  };

  const onSubmit = ({ firstName, lastName, role, phone }) => {
    if (role.code === 'custom') {
      // eslint-disable-next-line no-param-reassign
      role.code = null;
    }

    saveProfile({ firstName, lastName, role, phone });
  };

  return (
    <div className="cs-personal-information">
      <div className="profile-section">
        <div className="title">Personal Information</div>
        <form className="ui form" onSubmit={handleSubmit(onSubmit)}>
          <div className="field">
            <div className="two fields">
              <div className="field">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label>First Name</label>
                <input type="text" className={classNames({ error: errors.firstName })} {...register('firstName', { required: true })} placeholder="Enter First Name" />
                {errors.firstName && <div className="error-message">First Name is required.</div>}
              </div>
              <div className="field">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label>Last Name</label>
                <input type="text" className={classNames({ error: errors.lastName })} {...register('lastName', { required: true })} placeholder="Enter Last Name" />
                {errors.lastName && <div className="error-message">Last Name is required.</div>}
              </div>
            </div>
            <div className="two fields">
              <div className="field">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label>Company</label>
                <input type="text" {...register('company')} placeholder="Enter Company" disabled />
              </div>
              <div className="field">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label>Role</label>
                <div className="role-ctx">
                  <Controller
                    name="role"
                    control={control}
                    rules={{ required: true }}
                    // @ts-ignore
                    render={({ field: { value, onChange } }) => <Dropdown placeholder="Select your role" fluid search value={value?.code} options={options} onOptionSelect={(v) => onChange(selectRole(v))} />}
                  />
                  {role?.code === null && <input type="text" {...register('role.freeText')} placeholder="Enter Custom Role" />}
                </div>
              </div>
            </div>
            <div className="two fields">
              <div className="field">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label>Email Address</label>
                <input type="text" {...register('email')} placeholder="Enter Email Address" disabled />
              </div>
              <div className="field">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label>Phone</label>
                <input type="text" {...register('phone')} placeholder="Enter Phone number" />
              </div>
            </div>
          </div>
          <div className="buttons-ctx">
            <Button type="submit" mode={ButtonMode.PRIMARY} size={ButtonSize.MEDIUM} loading={isSaveLoading} disabled={isSaveLoading}>
              Save
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PersonalInformation;
